import React, { useEffect, useState } from "react";
import api from "../api/api";
import { useLocation } from "react-router-dom";

import {
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Input,
  Button,
  Tag,
  Empty,
  Badge,
  Pagination,
} from "antd";

import {
  ShoppingCartOutlined,
  SearchOutlined,
  HeartOutlined,
  EyeOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const ProductFrontList = () => {
  const location = useLocation();

  const query =
    new URLSearchParams(location.search).get("search") || "";

  // ================= STATES =================
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);

  const [loading, setLoading] = useState(true);

  const [searchValue, setSearchValue] = useState(query);

  const [currentPage, setCurrentPage] = useState(1);

  const productsPerPage = 8;

  // ================= FETCH PRODUCTS =================
  const fetchProducts = async (
    searchText = "",
    page = 1
  ) => {
    try {
      setLoading(true);

      const url = searchText
        ? `/products-front-lists/?search=${encodeURIComponent(
            searchText
          )}&page=${page}`
        : `/products-front-lists/?page=${page}`;

      const res = await api.get(url);

      // DJANGO PAGINATION RESULTS
      setProducts(res.data?.results || []);

      // TOTAL PRODUCTS COUNT
      setTotalProducts(res.data?.count || 0);

    } catch (error) {
      console.log(error);

      setProducts([]);
      setTotalProducts(0);

    } finally {
      setLoading(false);
    }
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    fetchProducts(query, currentPage);
  }, [query, currentPage]);

  // ================= SEARCH =================
  const handleSearch = (value) => {
    setSearchValue(value);

    // RESET TO PAGE 1
    setCurrentPage(1);

    fetchProducts(value, 1);
  };

  return (
    <div
      style={{
        background: "#f3f4f6",
        minHeight: "100vh",
        padding: "24px",
      }}
    >
      {/* ================= HEADER ================= */}
      <div
        style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "18px",
          marginBottom: "24px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <Row justify="space-between" align="middle" gutter={[20, 20]}>
          <Col xs={24} md={12}>
            <div>
              <Title
                level={2}
                style={{
                  margin: 0,
                  fontWeight: "700",
                }}
              >
                Discover Products
              </Title>

              <Text type="secondary">
                Search smart ranked products instantly
              </Text>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <Input
              size="large"
              placeholder="Search products..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) =>
                handleSearch(e.target.value)
              }
              style={{
                borderRadius: "12px",
                height: "50px",
              }}
            />
          </Col>
        </Row>
      </div>

      {/* ================= COUNT ================= */}
      {!loading && (
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <Text strong style={{ fontSize: "16px" }}>
            {totalProducts} Products Found
          </Text>

          <Tag color="blue">
            Smart Ranked Results
          </Tag>
        </div>
      )}

      {/* ================= LOADING ================= */}
      {loading ? (
        <div
          style={{
            height: "60vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </div>
      ) : products.length === 0 ? (
        <div
          style={{
            background: "#fff",
            padding: "60px 20px",
            borderRadius: "18px",
          }}
        >
          <Empty description="No products found" />
        </div>
      ) : (
        <>
          {/* ================= PRODUCTS ================= */}
          <Row gutter={[20, 20]}>
            {products.map((item, index) => (
              <Col
                key={item.id || index}
                xs={24}
                sm={12}
                md={8}
                lg={6}
              >
                <Badge.Ribbon
                  text={
                    item.quantity < 5
                      ? "Low Stock"
                      : "Available"
                  }
                  color={
                    item.quantity < 5
                      ? "red"
                      : "green"
                  }
                >
                  <Card
                    hoverable
                    style={{
                      borderRadius: "20px",
                      overflow: "hidden",
                      border: "none",
                      boxShadow:
                        "0 4px 18px rgba(0,0,0,0.06)",
                    }}
                    bodyStyle={{
                      padding: "18px",
                    }}
                    cover={
                      <div
                        style={{
                          position: "relative",
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: "260px",
                            objectFit: "cover",
                          }}
                        />

                        {/* ACTION BUTTONS */}
                        <div
                          style={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <Button
                            shape="circle"
                            icon={<HeartOutlined />}
                          />

                          <Button
                            shape="circle"
                            icon={<EyeOutlined />}
                          />
                        </div>
                      </div>
                    }
                  >
                    <Text
                      style={{
                        color: "#1677ff",
                        fontWeight: 600,
                      }}
                    >
                      {item.category || "Product"}
                    </Text>

                    <Title
                      level={5}
                      style={{
                        marginTop: 10,
                      }}
                    >
                      {item.name}
                    </Title>

                    <Text
                      strong
                      style={{
                        fontSize: 20,
                      }}
                    >
                      ${item.price}
                    </Text>

                    <div
                      style={{
                        marginTop: 8,
                      }}
                    >
                      <Text type="secondary">
                        Stock: {item.quantity}
                      </Text>
                    </div>

                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      block
                      style={{
                        marginTop: 15,
                        borderRadius: 10,
                        height: 42,
                        fontWeight: 600,
                      }}
                    >
                      Add To Cart
                    </Button>
                  </Card>
                </Badge.Ribbon>
              </Col>
            ))}
          </Row>

          {/* ================= PAGINATION ================= */}
          <div
            style={{
              marginTop: "50px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Pagination
              current={currentPage}
              total={totalProducts}
              pageSize={productsPerPage}
              showSizeChanger={false}
              onChange={(page) => {
                setCurrentPage(page);

                // SCROLL TO TOP
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProductFrontList;